"use client";

import { useState } from "react";

import { trackEvent } from "@/lib/analytics";
import { rateReading } from "@/lib/api/plans";
import { rateChatReading } from "@/lib/api/chat";

/**
 * Reading-level affirmation — the calibration signal (rule 44). One tap records
 * whether the reading rang true; the backend attributes it to every engine rule
 * that fired, building per-rule accuracy. Best-effort: a save error is a silent
 * no-op (the seeker already has their reading).
 *
 * `surface` selects where the rating lands: "report" (default) → the paid PDF
 * reading via /reports/{orderId}/rate; "chat" → the live chat reading via
 * /chat/{readingId}/rate-reading. Both upsert one vedic_reading_ratings row
 * keyed by the reading id, so chat readings (~100x the volume) feed the same
 * flywheel as paid reports. `orderId` is the reading id in both cases.
 */
export function ReportRating({
  orderId,
  planId,
  surface = "report",
}: {
  orderId: string;
  planId?: string;
  surface?: "report" | "chat";
}) {
  const [state, setState] = useState<"idle" | "down" | "done">("idle");
  const [note, setNote] = useState("");

  function send(thumbs: 1 | -1, comment?: string) {
    trackEvent("report_rated", {
      order_id: orderId,
      plan_id: planId,
      surface,
      thumbs,
      has_comment: Boolean(comment),
    });
    const rate = surface === "chat" ? rateChatReading : rateReading;
    void rate(orderId, { thumbs, comment: comment || undefined }).catch(() => {});
    setState(thumbs === -1 && !comment ? "down" : "done");
  }

  if (state === "done") {
    return (
      <p className="mt-3 text-[12px]" style={{ color: "var(--ish-text-muted)" }}>
        🙏 Thank you — this helps us read your chart more truly.
      </p>
    );
  }

  const pill =
    "rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-50";

  return (
    <div className="mt-3">
      <p className="text-[12px] font-medium" style={{ color: "var(--ish-text-muted)" }}>
        Did this reading ring true for you?
      </p>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={() => send(1)}
          className={pill}
          style={{
            color: "var(--ish-accent)",
            border: "1px solid color-mix(in srgb, var(--ish-accent) 35%, transparent)",
          }}
        >
          Yes, it did
        </button>
        <button
          type="button"
          onClick={() => send(-1)}
          className={pill}
          style={{
            color: "var(--ish-text-muted)",
            border: "1px solid color-mix(in srgb, var(--ish-text-muted) 30%, transparent)",
          }}
        >
          Not quite
        </button>
      </div>
      {state === "down" && (
        <div className="mt-2 flex gap-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={280}
            placeholder="What felt off for you? (optional)"
            className="flex-1 rounded-lg px-3 py-1.5 text-[12px]"
            style={{
              border: "1px solid color-mix(in srgb, var(--ish-text-muted) 25%, transparent)",
            }}
          />
          <button
            type="button"
            onClick={() => send(-1, note.trim())}
            className={pill}
            style={{ background: "var(--ish-accent)", color: "white" }}
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
}
